<template>
    <div class="card mt-4 mb-6">
        <div class="card-header">
            <div class="card-title">
                <ol class="breadcrumb breadcrumb-arrows" aria-label="breadcrumbs">
                    <li class="breadcrumb-item"><a href="/">Home</a></li>
                    <li class="breadcrumb-item"><a :href="'/products/'+this.$route.params.groupId">{{
                        categoryName
                        }}</a>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page"><a href="#">{{ product.name }}</a></li>
                </ol>
            </div>
            <div class="card-options">
            </div>
        </div>
        <div class="card-body">
            <div class="row">
                <div class="col-sm-12 col-md-6">
                    <div class="card mb-sm-3">
                        <div class="card-body">
                            <!-- The box has the photo's shape from the start, so nothing below moves when it arrives. -->
                            <div class="product-photo rounded-3 shadow-sm" data-testid="product-photo">
                                <img v-if="photoState === 'failed'" src="/LHB-LogoType.svg" alt="" class="product-photo-fallback image-loaded" />
                                <img v-else-if="product.productImage"
                                     :key="product.productImage"
                                     :src="generateImage(product)" :alt="product.name" decoding="async"
                                     :class="{ 'image-loaded': photoState === 'loaded' }"
                                     @load="imageLoadedFor = product.productImage"
                                     @error="imageFailedFor = product.productImage" />
                                <div v-if="photoState === 'loading'" class="product-photo-placeholder" aria-hidden="true"></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md-6 mt-md-0">
                    <div class="card mt-sm-6 mt-md-0 h-100">
                        <div class="card-body">
                            <div class="alert alert-info mb-2" role="alert" v-if="hasText(product.status)">
                                <div class="d-flex">
                                    <div>
                                        <!-- Download SVG icon from http://tabler-icons.io/i/info-circle -->
                                        <svg xmlns="http://www.w3.org/2000/svg" class="icon alert-icon" width="24"
                                             height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"
                                             fill="none" stroke-linecap="round" stroke-linejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                            <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                                            <path d="M12 9h.01"></path>
                                            <path d="M11 12h1v4h1"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        {{ product.status }}
                                    </div>
                                </div>
                            </div>
                            <div class="alert alert-danger" role="alert" v-if="loadError">
                                We couldn't load this product. Please check your connection and reload the page, or
                                <router-link :to="'/products/' + $route.params.groupId">go back to the category</router-link>.
                            </div>
                            <h1 class="product-name">{{ product.name }}</h1>
                            <span class="product-price">{{ formatCurrency(product.price) }} {{ priceUnitLabel }}</span><br/>
                            <span class="text-blue"
                                  v-if="product.discountAmount > 0">{{ Math.min(product.discountAmount, 100) }}% Off</span><span
                                class="text-blue" v-if="product.discountQty > 0"> when you buy {{ product.discountQty }} or more</span>
                            <div class="mt-2 mb-3 card-body markdown" v-html="productDescription"></div>
                            <div class="mb-3 bold" v-if="hasText(product.allergens)">Allergen information:
                                {{ product.allergens }}
                            </div>
                            <div class="mb-3 text-secondary" v-else>For allergen information about this product, please
                                ask us before you order.
                            </div>
                            <div class="mb-3 bold" v-if="product.itemDeposit && !fixedDeposit" data-testid="product-deposit">A non-transferable, non-refundable deposit
                                of {{ formatCurrency(product.itemDeposit) }} applies to this product.
                            </div>
                            <span class="mt-2 mb-2 btn btn-ghost-primary d-inline-block" style="width: auto" v-if="hasSizeGuide" data-bs-toggle="modal" data-bs-target="#sizeGuideModal"><IconInfoCircle></IconInfoCircle> Size Guide</span>
                            <div class="mb-3 row">
                                <div class="col col-sm-12 col-md-6">
                                    <label for="weight-select" class="form-label" v-if="product.weightUnit !== 'each'">Select
                                        Weight</label>
                                    <label for="weight-select" class="form-label" v-else-if="optionsAreWeights">Select
                                        Size</label>
                                    <label for="weight-select" class="form-label" v-else>Select
                                        Quantity</label>
                                    <!-- WP-40: chosen by option id, so two options with the same value (lamb rump's
                                         "Thick cut" and "Square cut") keep their own label in the basket. -->
                                    <select class="form-select" :class="{'is-invalid': !validWeight}" id="weight-select"
                                            :aria-describedby="selectedSizeHint ? 'weight-select-hint' : null"
                                            v-model="selectedOptionId">
                                        <option v-for="option in product.weightOptions" :key="option.weightOptionId"
                                                :value="option.weightOptionId"
                                                :disabled="option.qty === 0 || option.unavailable">
                                            {{ option.name }} <span v-if="option.unavailable">OUT OF STOCK</span>
                                        </option>
                                    </select>
                                    <div v-if="selectedSizeHint" id="weight-select-hint" class="form-hint mt-1" data-testid="size-hint">
                                        {{ selectedSizeHint }}
                                    </div>
                                    <div v-if="!validWeight" class="text-danger small mt-1" role="alert">
                                        Please choose {{ product.weightUnit !== 'each' ? 'a weight' : (optionsAreWeights ? 'a size' : 'a quantity') }} first.
                                    </div>

                                </div>
                                <div class="col col-sm-12 col-md-6">
                                    <label class="form-label" v-if="product.weightUnit !== 'each' || optionsAreWeights">Estimated
                                        Cost:</label>
                                    <label class="form-label" v-else>Cost:</label>
                                    <!-- The tiles' "from" price (WP-39, worker lib/fromPrice.ts) repeats this
                                         arithmetic. The second line is v-else, so a NULL discountAmount still
                                         shows the cost (it used to show neither line). -->
                                    <h3 class="mt-1"
                                        v-if="product.discountAmount > 0 && this.selectedWeight >= product.discountQty">
                                        <s>{{ formatCurrency(this.selectedWeight * product.price) }}</s>
                                        {{ formatCurrency((this.selectedWeight * product.price) * ((100 - Math.min(product.discountAmount, 100)) / 100)) }}
                                    </h3>
                                    <h3 class="mt-1" v-else>
                                        {{ formatCurrency((this.selectedWeight * product.price)) }}
                                    </h3>
                                </div>
                            </div>
                            <div class="mb-3 row" v-if="hasCustomisationOptions">
                                <!-- FE-116: the choices are shown open, each with its own label. -->
                                <fieldset class="col-12" data-testid="customisations">
                                    <legend class="h3 mb-2">Customisation Options</legend>
                                    <template v-for="customisation in product.customisations" :key="customisation.customisationId">
                                        <div class="mb-3" v-if="customisation.customisationOptions && customisation.customisationOptions.length > 0">
                                            <label class="form-label mb-1" :for="'customisation-' + customisation.customisationId">
                                                {{ customisation.customisationName }}
                                            </label>
                                            <div class="form-hint mb-2" v-if="hasText(customisation.customisationText)"
                                                 :id="'customisation-hint-' + customisation.customisationId">
                                                {{ customisation.customisationText }}
                                            </div>
                                            <select class="form-select"
                                                    :id="'customisation-' + customisation.customisationId"
                                                    :class="{'is-invalid': missingOptions.includes(customisation.customisationId)}"
                                                    :aria-describedby="hasText(customisation.customisationText) ? 'customisation-hint-' + customisation.customisationId : null"
                                                    v-model="selectedOptions[customisation.customisationId]">
                                                <!-- Only a customisation with no default starts unchosen. -->
                                                <option v-if="!customisation.customisationOptions.some(o => o.customisationDefault === 1)"
                                                        value="" disabled>Please choose</option>
                                                <option
                                                        v-for="option in customisation.customisationOptions"
                                                        :key="option.optionID"
                                                        :value="option.optionID"
                                                >
                                                    {{ option.customisationOptionName }}
                                                </option>
                                            </select>
                                            <div v-if="missingOptions.includes(customisation.customisationId)" class="text-danger small mt-1" role="alert">
                                                Please choose an option for {{ customisation.customisationName }}.
                                            </div>
                                        </div>
                                    </template>
                                </fieldset>
                            </div>
                        </div>
                        <div class="card-footer">
                            <div class="d-flex flex-column align-items-end">
                                <div class="alert alert-success alert-dismissible w-100 mb-2" role="status"
                                     v-if="showBasketAlert">
                                    <div class="d-flex">
                                        <div>
                                            <!-- Download SVG icon from http://tabler-icons.io/i/check -->
                                            <svg xmlns="http://www.w3.org/2000/svg" class="icon alert-icon" width="24"
                                                 height="24"
                                                 viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none"
                                                 stroke-linecap="round"
                                                 stroke-linejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M5 12l5 5l10 -10"></path>
                                            </svg>
                                        </div>
                                        <div>
                                            <div>Added to basket</div>
                                            <div class="mt-2 d-flex flex-wrap gap-2">
                                                <router-link class="btn btn-primary btn-sm" to="/basket" data-testid="view-basket">View basket</router-link>
                                                <router-link class="btn btn-outline-secondary btn-sm" :to="'/products/' + $route.params.groupId">Keep shopping</router-link>
                                            </div>
                                        </div>
                                    </div>
                                    <button type="button" class="btn-close" @click="closeBasketAlert()" aria-label="Dismiss"></button>
                                </div>
                                <div v-if="basketError" class="text-danger mb-2 w-100" role="alert">{{ basketError }}</div>
                                <button type="button" class="btn btn-primary" @click="addToBasket()">Add to basket</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div id="sizeGuideModal" class="modal modal-blur fade">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content">
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                <div class="modal-status bg-primary"></div>
                <div class="modal-body">
                    <h3>Size Guide</h3>
                    <div class="table table-responsive w-100" v-html="sizeGuide">

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import api from "@/services/api";
import { setPageTitle } from "@/services/pageTitle";
import { cleanLabel, describeSize } from "@/services/sizeText";
import {IconInfoCircle} from '@tabler/icons-vue'
import { isWholeOrderDeposit, loadDepositPolicy } from "@/services/depositPolicy";
import DOMPurify from "dompurify";
import MarkdownIt from 'markdown-it';
const md = new MarkdownIt();

export default {
    name: "ProductDetails",
    methods: {
        hasText(value) {
            return typeof value === 'string' ? value.trim().length > 0 : !!value;
        },
        formatCurrency(amountInPence) {
            const pounds = amountInPence / 100;
            return new Intl.NumberFormat('en-GB', {style: 'currency', currency: 'GBP'}).format(pounds);
        },
        generateImage(item) {
          return `https://larkhallbutchers.co.uk/cdn-cgi/image/width=640/${item.productImage}`
        },
        closeBasketAlert() {
            this.showBasketAlert = false;
        },
        async addToBasket() {
            let basketId = localStorage.getItem('basketId2026');

            if (!basketId) {
                try {
                    const response = await api.get('/api/basket/new');
                    basketId = response.data.basketId;
                    localStorage.setItem('basketId2026', basketId);
                } catch (error) {
                    console.error('Error fetching new basketId:', error);
                    return;
                }
            }

            if (this.selectedWeight) {

                const basketItem = {
                    item: this.product.name,
                    sku: this.product.sku,
                    unit: this.product.weightUnit,
                    price: this.product.price,
                    deposit: this.product.itemDeposit,
                    discountQty: this.product.discountQty,
                    discountAmount: this.product.discountAmount,
                    image: this.product.productImage,
                    selectedWeight: this.selectedWeight,
                    // L-82 (WP-40): the option's label as the customer saw it, so the basket can say
                    // "Large Three (about 4 kg)" rather than "4kg". Priced from selectedWeight as before.
                    selectedWeightName: this.selectedOption ? cleanLabel(this.selectedOption.name) : null,
                    weightOptionId: this.selectedOption ? this.selectedOption.weightOptionId : null,
                    id: this.product.productID,
                    customisations: [],
                }
                // FE-116: a customisation with nothing chosen (it had no default) is asked for,
                // rather than throwing here and adding nothing.
                const missing = [];
                for (const c of (this.product.customisations || [])) {
                    const options = c.customisationOptions || [];
                    if (options.length === 0) continue;
                    const x = options.find(o => o.optionID === this.selectedOptions[c.customisationId])
                    if (!x) {
                        missing.push(c.customisationId);
                        continue;
                    }
                    basketItem.customisations.push({
                        name: c.customisationName,
                        value: x.customisationValue,
                        text: x.customisationOptionName,
                        id: x.optionID,
                        customisationId: c.customisationId
                    })
                }
                this.validWeight = true;
                this.basketError = '';
                this.missingOptions = missing;
                if (missing.length > 0) {
                    this.showBasketAlert = false;
                    return;
                }
                try {
                    await api.post('/api/basket', {basketGUID: basketId, basketItem})
                    this.showBasketAlert = true;
                    window.dispatchEvent(new CustomEvent('basket-changed'));
                } catch (e) {
                    // FE-13: only say "added" when the item really was saved.
                    this.showBasketAlert = false;
                    this.basketError = "Sorry, we couldn't add that to your basket. Please try again.";
                }
            } else {
                this.validWeight = false;
            }
        }
    },
    async mounted() {
        loadDepositPolicy();
        try {
            this.product =(await api.get('/api/products/sku/' + this.$route.params.productId)).data
            // FE-118: the product's name as the page title.
            if (this.product && this.product.name) setPageTitle(this.product.name);
            if (this.product.sizeGuide != null && this.product.sizeGuide !== '' && this.product.sizeGuide >= 0) {
                // Its own try: a failed size guide must not skip the default options below.
                try {
                    this.rawSizeGuide = (await api.get('/api/products/size-guide/' + this.product.sizeGuide)).data;
                    this.hasSizeGuide = true;
                } catch (e) {
                    this.hasSizeGuide = false;
                }
            }
            this.product.customisations.forEach(customisation => {
                const defaultOption = customisation.customisationOptions.find(option => option.customisationDefault === 1);
                // No default: start on "Please choose" ('') so the customer has to pick one.
                this.selectedOptions[customisation.customisationId] = defaultOption ? defaultOption.optionID : '';
            });
        } catch (e) {
            this.loadError = true;
        }
    },
    components: {
        IconInfoCircle
    },
    computed: {
        // WP-37: with one deposit for the whole order (fixed or a percentage), a product has no
        // deposit of its own. While the policy loads, or if it fails, the product's deposit is shown as before.
        fixedDeposit() {
            return isWholeOrderDeposit();
        },
        selectedOption() {
            const options = (this.product && this.product.weightOptions) || [];
            return options.find(option => option.weightOptionId === this.selectedOptionId) || null;
        },
        // The option's value (the weight or quantity everything is priced from), as before.
        selectedWeight() {
            return this.selectedOption ? this.selectedOption.value : null;
        },
        // L-82 (WP-40): the chosen option as the basket and emails will describe it, shown under the
        // select when the label alone does not say the weight ("Large Three" is priced as 4 kg).
        selectedSizeHint() {
            const o = this.selectedOption;
            const label = o ? cleanLabel(o.name) : null;
            if (!label) return '';
            const unit = this.product.weightUnit;
            const text = describeSize(unit, o.value, label);
            if (text === label || !text.includes('about ')) return '';
            return `Estimated weight: about ${describeSize(unit === 'each' ? 'kg' : unit, o.value, null)}`;
        },
        // 'loading' until the product is known and its photo has arrived; 'failed' when the product
        // has no photo or it could not be fetched (the shop's logotype is shown, as on the tiles).
        photoState() {
            if (!this.product || !this.product.name) return 'loading';
            const image = this.product.productImage;
            if (!image || this.imageFailedFor === image) return 'failed';
            return this.imageLoadedFor === image ? 'loaded' : 'loading';
        },
        // Some products are sold by weight band but carry the unit "each" in the catalogue (the whole
        // turkey: options "4-5kg" ... "10kg+"). Their options are sizes, not quantities, and the price
        // is per kg, so the cost is an estimate.
        optionsAreWeights() {
            const options = (this.product && this.product.weightOptions) || [];
            return this.product.weightUnit === 'each' && options.length > 0
                && options.every(option => /\d\s*kg/i.test(option.name || ''));
        },
        priceUnitLabel() {
            if (this.optionsAreWeights) return 'per kg';
            return this.product.weightUnit === 'each' ? 'each' : 'per ' + this.product.weightUnit;
        },
        productDescription() {
            let prdouctDescription = ""
            try {
                prdouctDescription = this.product.description.toString()
            } catch (e) {
                console.log(e)
            }
            return DOMPurify.sanitize(md.render(prdouctDescription))
        },
        sizeGuide() {
          let sizeGuide = ""
            try {
              sizeGuide = this.rawSizeGuide.toString()
            } catch (e) {
                console.log(e);
            }
            return DOMPurify.sanitize(md.render(sizeGuide))
        },
        hasCustomisationOptions() {
            try {
                return this.product.customisations.some(
                    customisation => customisation.customisationOptions && customisation.customisationOptions.length > 0
                );
            } catch (err) {
                console.log("No customisations")
                return false
            }
        }

    },
    data() {
        return {
            categoryName: this.$route.params.groupId.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ').replace(/And/g, "&"),
            selectedOptionId: null,
            selectedOptions: {},
            product: {},
            // Which photo (by productImage) has loaded or failed, so a change of product starts again.
            imageLoadedFor: false,
            imageFailedFor: false,
            validWeight: true,
            missingOptions: [],
            loadError: false,
            showBasketAlert: false,
            basketError: '',
            hasSizeGuide: false,
            rawSizeGuide: ""
        }
    },
    watch: {
        selectedOptionId() {
            if (this.selectedWeight) {
                this.validWeight = true;
            }
        }
    }
}
</script>




<style scoped>
/* The price is the second-strongest thing on the page, after the product name. */
.product-name {
    font-size: 1.75rem;
    line-height: 1.2;
    margin-bottom: 0.25rem;
}
/* Same shape, neutral background and fade as the product tiles. */
.product-photo {
    position: relative;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    background-color: #f8f9fa;
}
.product-photo img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 0.3s ease;
}
.product-photo img.image-loaded {
    opacity: 1;
}
.product-photo img.product-photo-fallback {
    object-fit: contain;
    padding: 15%;
}
.product-photo-placeholder {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, #f8f9fa 25%, #eef0f2 50%, #f8f9fa 75%);
    background-size: 200% 100%;
    animation: product-photo-shimmer 1.5s infinite;
}
@keyframes product-photo-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}
@media (prefers-reduced-motion: reduce) {
    .product-photo-placeholder { animation: none; }
    .product-photo img { transition: none; }
}
.product-price {
    display: inline-block;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.3;
}
</style>
